// MPD-190: the monthly-report status of a month's CU, one rule for the Overview badge, the
// scorecard and the monthly PDF. The API decides it with the golden reconcile's tolerance (±5 %):
// "matches" within it, "differs" beyond it (the signed difference is shown, never overwritten),
// "none" without a report. Supplier-visible text names the source neutrally, never "Heart".
import type { DosLayer, FacitStatus } from './landing'
import { formatPercent, formatSignedPercent } from './format'
import { toWinAnsi } from './util/pdfText'

// MPD-193: the scorecard mark as the monthly PDF prints it. jsPDF's helvetica is WinAnsi and has
// neither ✓ nor ≠, so `text` is WinAnsi bytes in `font`: ZapfDingbats "3" is its ✓, and differs
// is spelled "Differs +6.2 %" in helvetica.
export interface PdfMark {
  text: string
  font: 'helvetica' | 'zapfdingbats'
}
export const PDF_TICK: PdfMark = { text: '3', font: 'zapfdingbats' }
export const PDF_NO_MARK: PdfMark = { text: '—', font: 'helvetica' }

export interface FacitBadge {
  status: FacitStatus
  label: string // the short status on screen
  tip: string // its ⓘ
  mark: string // the scorecard value: ✓, ≠ ±x % or —
  pdfMark: PdfMark // the same value in the PDF scorecard: ✓, Differs ±x % or —
  report: string // the line the monthly PDF prints
}

export function facitBadge(dos: Pick<DosLayer, 'validated' | 'facit_delta_pct' | 'facit_status' | 'facit_tolerance_pct'>): FacitBadge {
  // An API from before MPD-190 sends only `validated`.
  const status: FacitStatus = dos.facit_status ?? (dos.validated ? 'matches' : 'none')
  const tolerance = `±${formatPercent(dos.facit_tolerance_pct ?? 5, 0)}`
  const delta = dos.facit_delta_pct ?? 0
  if (status === 'matches') {
    const label = `Matches monthly report (±${formatPercent(Math.abs(delta))})`
    const tip = `This month’s CU compared with the monthly report: difference ±${formatPercent(Math.abs(delta))}, within the ${tolerance} tolerance of the monthly reconciliation.`
    return { status, label, tip, mark: '✓', pdfMark: PDF_TICK, report: `${label}. ${tip}` }
  }
  if (status === 'differs') {
    const label = `Differs from the monthly report by ${formatSignedPercent(delta)}`
    const tip = `Our CU comes from MinPrilla’s order data; the monthly report is compiled separately. Differences above ${tolerance} are shown as they are, never overwritten.`
    const pdfMark: PdfMark = { text: toWinAnsi(`Differs ${formatSignedPercent(delta)}`), font: 'helvetica' }
    return { status, label, tip, mark: `≠ ${formatSignedPercent(delta)}`, pdfMark, report: `${label}. ${tip}` }
  }
  const label = 'Not yet reconciled'
  const tip = 'There is no monthly report for this month yet.'
  return { status, label, tip, mark: '—', pdfMark: PDF_NO_MARK, report: `${label}. ${tip}` }
}
